import { EChartsOption } from 'echarts';
import { JgOption, componentProps, JgLineData } from './declare';
import { ref } from 'vue';
import { getLineOptions, colorPlan, iconLinePath, iconBarPath } from './config';

const YAXIS_OFFSET = 30;
type Series = EChartsOption['series'];

function createOptions(props: componentProps, options: any, lineData: JgLineData[]) {
  const legendData: string[] = [];
  const xAxisData = new Set<string>();
  const series: Series = [];
  const yAxis: EChartsOption['yAxis'][] = [];
  let noYAxisShowSplitLine = true;
  const legendSelectedArr = props.legendSelected
    ? Object.values(props.legendSelected).filter(item => item)
    : null;
  let offset = 0;
  const unitList: string[] = [];
  const yp = props.yPosition ?? [];
  const multiYaxis = (() => {
    const arr = Array(lineData.length);
    for (let i = 0; i < arr.length; i++) {
      arr[i] = yp.includes((i + 1) as never);
    }
    return arr;
  })();
  const position: Array<'left' | 'right'> = (() => {
    const p = ['left', 'right'];
    let f = 0;
    const arr = Array(lineData.length);
    for (let i = 0; i < arr.length; i++) {
      if (yp.includes((i + 1) as never)) {
        arr[i] = p[f++];
      } else {
        arr[i] = p[f];
      }
    }
    return arr;
  })() as Array<'left' | 'right'>;
  lineData.forEach((v, index) => {
    unitList.push(v.unit ?? '');
    v.name && legendData.push(v.name);
    const values: any[] = [];
    let tempObj = {};
    v.children.forEach(child => {
      xAxisData.add(child.key);
      // 折线拐点特殊的标识
      if (child.symbol) {
        tempObj = {
          value: child.value,
          symbol: child.symbol ? 'triangle' : '',
          meta: child.meta,
        };
        values.push(tempObj);
      } else {
        values.push(child.value);
      }
    });
    series.push(
      Object.assign(
        {
          name: v.name,
          type: v.type ?? 'line',
          data: values,
          // 是否连接空数据
          connectNulls: true,
          smooth: true,
          showSymbol: true,
          symbolSize: values.some(v => v?.symbol) ? 8 : 6,
          animationDuration: 800,
          animationEasing: 'cubicInOut',
          barMaxWidth: v.type ? 40 : '',
        },
        multiYaxis && {
          yAxisIndex: multiYaxis[index] ? index : 0 ?? 0,
        },
        props.dotted && {
          lineStyle: {
            type: props.dotted[index] ? 'dotted' : 'solid',
          },
        },
        props.dot && {
          itemStyle: {
            opacity: props.dot[index] ? 1 : 0,
          },
        },
        props.area &&
          props.area[index] && {
            areaStyle: {
              color: {
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                global: false,
                type: 'liner',
                colorStops: [
                  {
                    offset: 0,
                    color: colorPlan[index].replace('1)', '0.2)'),
                  },
                  {
                    offset: 1,
                    color: colorPlan[index].replace('1)', '0)'),
                  },
                ],
              },
            },
          },
      ),
    );

    /** 获取 yAxis */
    const unit = v.unit || '';
    let showSplitLine: boolean;
    const axisLabelColor = multiYaxis ? colorPlan[index] : '#B9BCC3';
    if (legendSelectedArr) {
      offset += legendSelectedArr[index] ? YAXIS_OFFSET : 0;
      showSplitLine = legendSelectedArr[index] && noYAxisShowSplitLine;
      if (showSplitLine) {
        noYAxisShowSplitLine = false;
      }
    } else {
      offset += multiYaxis && multiYaxis[index] ? YAXIS_OFFSET : 0;
      showSplitLine = index === 0;
    }
    //  `offset ? offset - OFFSET : 0` 确保第一个显示的 yAxis offset 是 0
    yAxis.push(
      getYAxis({
        position: position[index] ?? 'left',
        offset: offset && position[index] === 'left' ? offset - YAXIS_OFFSET : 0,
        showSplitLine,
        axisLabelColor: '#B9BCC3',
        unit,
      }),
    );
  });

  /** 把上面获取到的 option 赋值到 options.value 上*/
  const option: EChartsOption = JSON.parse(JSON.stringify(options.value));
  if (option.xAxis) {
    (option.xAxis as any).data = Array.from(xAxisData);
  }
  option.series = series;
  option.yAxis = yAxis as any;
  renderLegent(props, option, lineData);
  renderTooltip(props, option, unitList);
  renderGraphic(lineData, option);
  return option;
}

// 渲染图例
function renderLegent(props: componentProps, option: any, lineData: JgLineData[]) {
  /** 把上面获取到的 option 赋值到 options.value 上*/
  if (option.legend && !Array.isArray(option.legend)) {
    option.legend.data = getLegend(lineData);
    // 隐藏图例、暂无数据时 图例不展示
    option.legend.show =
      !props.hideLegend &&
      lineData.every((v: { children: string | any[] }) => v.children.length !== 0);
    props.legendSelected && (option.legend.selected = props.legendSelected);
  }
}

// 柱状图图例图标是圆形 折线图是图片
function getLegend(lineData: JgLineData[]) {
  return lineData.map(item => {
    return {
      name: item.name,
      icon: item.type === 'bar' ? iconBarPath : iconLinePath,
    };
  });
}

// 渲染空数据情况
function renderGraphic(lineData: JgLineData[], option: any) {
  if (lineData.every((v: { children: string | any[] }) => v.children.length === 0)) {
    option.graphic = [
      {
        type: 'image',
        top: 'center',
        right: 'center',
        z: 1,
        style: {
          image: require('@/assets/console/nodata.png'),
          width: 119,
          height: 104,
        },
      },
    ].filter(Boolean);
  }
}

// tooltip渲染
function renderTooltip(props: componentProps, option: any, unitList: string[]) {
  if (option.tooltip && !Array.isArray(option.tooltip)) {
    option.tooltip.formatter = (params: any): string => {
      const toolTip = `${
        params.some((w: any) => w.data.symbol === 'triangle') ? props.toolTitle : ''
      }`;
      let res = `<div><p> ${params[0].name}${toolTip} </p></div>`;
      let marker = '',
        lineMarker = '',
        barMarker = '';
      for (let i = 0; i < params.length; i++) {
        if (params[i].data !== undefined) {
          lineMarker = `
            <span style="display: flex;align-items: center;margin-right:6px;transform: translateY(-1px);">
              <span style="display: inline-block;width: 4px;height: 2px;background: ${params[i].color}"></span>
              <span style="display: inline-block;width: 6px;height: 6px;background: ${params[i].color}; border-radius: 50%;margin: 0 1px;";></span>
              <span style="display: inline-block;width: 4px;height: 2px;background: ${params[i].color}"></span>
            </span>`;
          barMarker = `
          <span style="display: flex;align-items: center;margin-right:14px;transform: translateY(-1px);">
            <span style="display: inline-block;width: 8px;height: 8px;background: ${params[i].color};border-radius: 1px;"></span>
          </span>`;
          const percentData =
            unitList[i] === '%'
              ? params[i].value + (params[i].value === 0 ? '.00' : '') + unitList[i]
              : params[i].value;
          // 柱状图展示 折线图展示样式不同
          marker = params[i].seriesType === 'bar' ? barMarker : lineMarker;
          res += `<p style='display: flex;align-items: center;justify-content: space-between;'>
              <span style="display: flex;">${marker}${params[i].seriesName}</span>
              <span style="margin-left:24px;">${percentData}</span>
              </p>`;
        }
      }
      return res;
    };
    props.pointer &&
      (option.tooltip.axisPointer = {
        type: 'line',
        lineStyle: {
          type: props.pointer === 'dashed' ? 'dashed' : 'solid',
        },
      });
    option.tooltip.trigger = 'axis';
  }
}

function getYAxis({
  position = 'left',
  offset,
  showSplitLine,
  axisLabelColor,
  unit,
}: {
  position: 'left' | 'right';
  offset: number;
  showSplitLine: boolean;
  axisLabelColor: string;
  unit: string;
}): EChartsOption['yAxis'] {
  return {
    type: 'value',
    scale: true,
    axisLine: {
      show: false,
      lineStyle: {
        color: axisLabelColor,
      },
    },
    position,
    offset,
    axisTick: {
      show: false,
    },
    axisLabel: {
      color: axisLabelColor,
      fontSize: 12,
      formatter: `{value}${unit}`,
    },
    splitLine: {
      show: showSplitLine,
      lineStyle: {
        color: '#e6eaee',
        type: 'dashed',
      },
    },
    splitNumber: 4,
    min: 0,
  };
}

//导出option配置
export function exportOption(props: any, lineData: JgLineData[]): JgOption {
  // 获取默认配置项
  const options = ref(getLineOptions());
  const option: JgOption = createOptions(props, options, lineData);
  // 合并配置项
  const newOption = Object.assign(option, props.option);
  return newOption;
}
